import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Button,
  Checkbox,
  TextField,
  Autocomplete,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TablePagination,
  TableBody,
  TableCell,
  styled,
  tableCellClasses,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Delete } from "@mui/icons-material";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../../Components/ToastComponent/ToastNotification";
import TextInput from "../../../Components/InputComponents/TextInput";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import TextAreaInput from "../../../Components/InputComponents/TextAreaInput";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import {
  axiosAPI,
  axiosupload,
} from "../../../Components/AxiosComponents/Axios";
import { Link } from "react-router-dom";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

function DocumentSubmission({ candidateID, countryID, professionID, JAID }) {
  const [documentData, setDocumentData] = useState({
    comment: "",
    docid: "",
  });
  const [reload, setReload] = useState(false);

  const [documentFile, setdocumentFile] = useState(null);
  const [docCheckList, setDocCheckList] = useState([]);
  const [errors, setErrors] = useState({});

  const [docTable, setDocTable] = useState([]);

  const validateForm = () => {
    const mandatoryFields = [{ field: "docid", label: "Document type" }];

    let formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{10}$/; // 10-digit phone validation
    for (let { field, label, type } of mandatoryFields) {
      const value = documentData[field];

      if (!value) {
        formErrors[field] = `${label} is required.`;
      } else if (type === "email" && !emailRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
      } else if (type === "phone" && !phoneRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
      }
    }
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      // Display the errors as toast notifications
      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }
    return true;
  };

  const handledocumentChange = (event) => {
    const file = event.target.files[0];
    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2); // rounded to 2 decimal places

    if (file.size > MAX_SIZE_BYTES) {
      infoNotify(
      `File size should be less than ${MAX_SIZE_MB} MB. Uploaded file size is ${fileSizeMB} MB`
      );
      return;
    }

    setdocumentFile(event.target.files[0]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrors({
      ...errors,
      [name]: "",
    });

    setDocumentData({
      ...documentData,
      [name]: value,
    });
  };

  const getDocumentTableData = async () => {
    try {
      const res = await axiosAPI.get(
        `/Candidate/getCandidateDocuments/${candidateID}/${JAID}`
      );
      setDocTable(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getDocumentList = async () => {
    try {
      const res = await axiosAPI.post(
        `/RecruitmentProcedureMapping/listbycountryProfession`,
        {
          countryId: countryID,
          professionId: professionID,
        }
      );
      setDocCheckList(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const deletefn = (id) => {
    axiosAPI
      ?.delete(`/Candidate/deleteCandidateDocumentById/${id?.CDS_Id}`)
      .then((res) => {
        if (res?.data?.success) {
          getDocumentTableData();
        } else {
          errorNotify("Couldn't Delete document.");
        }
      })
      .catch((err) => {});
  };

  const [isDisabled, setIsDisabled] = useState(false);

  const savefn = () => {
    if (validateForm()) {
      if (documentFile) {
        setIsDisabled(true);
        const formData = new FormData();

        formData.append("CandidateDocument", documentFile);
        formData.append("Comment", documentData?.comment);
        formData.append("CheckListId", documentData?.docid);
        formData.append("CandidateId", candidateID);

        axiosupload
          ?.post(`/Candidate/addCandidateDocument`, formData)
          .then((res) => {
            if (res?.data?.success) {
              successNotify(res?.data?.data?.message);
              setReload(!reload);
              setDocumentData({ comment: "", docid: "" });
              setdocumentFile(null);
            }
            setIsDisabled(false);
          })
          .catch((err) => {
            setIsDisabled(false);
          });
      } else {
        errorNotify("Document is Required");
        setIsDisabled(false);
      }
    }
  };

  useEffect(() => {
    getDocumentTableData();
    if (countryID && professionID) {
      getDocumentList();
    }
  }, [countryID, reload]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        my={2}
      >
        Document Submission
      </Typography>

      <Grid
        container
        spacing={2}
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: 2,
          backgroundColor: "#fff", // Optional: card background
        }}
      >
        {/* Document type */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Document type <span style={{ color: "red" }}> * </span>
          </Typography>

          <SelectInput
            value={documentData.docid}
            name="docid"
            onChange={handleInputChange}
            options={
              docCheckList?.Documents
                ? docCheckList?.Documents?.map((p) => ({
                    label: p?.DocumentName,
                    value: p?.DocumentID,
                  }))
                : []
            }
            placeholder="Select Document"
            error={!!errors.docid}
          />
        </Grid>

        {/* Comment */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Comment
          </Typography>
          <TextInput
            label="Comment"
            placeholder="Enter your Comment"
            name="comment"
            value={documentData.comment}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Document Upload */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Document Upload <span style={{ color: "red" }}> * </span>
          </Typography>
          <input
            // accept=".pdf,.doc,.docx"
            style={{ display: "none" }}
            id="picture-upload"
            type="file"
            onChange={handledocumentChange}
          />
          <label htmlFor="picture-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              sx={{
                backgroundColor: "#3498DB",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                mt: 1,
              }}
            >
              {documentFile ? documentFile?.name : "Choose File"}
            </Button>
          </label>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12} md={12}>
          <CommonButton
            variant="contained"
            customColor="#007BFF"
            onClick={savefn}
            disabled={isDisabled}
          >
            {isDisabled ? "Saving..." : `Save`}
          </CommonButton>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "90vw",
          overflowX: "auto",
          margin: "10px auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell>CheckList Name</StyledTableCell>
              <StyledTableCell>Comment</StyledTableCell>
              <StyledTableCell>File</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docTable && docTable?.length > 0 ? (
              docTable
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>{log?.CDS_CheckListName}</StyledTableCell>
                    <StyledTableCell>
                      {log?.CDS_Comment ? log?.CDS_Comment : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Link
                        target="_blank"
                        to={`${process.env.REACT_APP_BASE_URL}/${log?.CDS_DocumentPath}`}
                      >
                        {log?.CDS_DocumentName}
                      </Link>
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedDoc(log); // Save current item
                          setOpenConfirm(true); // Open confirmation modal
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "#0977BC",
            mt: 3,
            mb: 2,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              mb: 1,
              fontFamily: "Geist, sans-serif",
            }}
          >
            Are you sure you want to delete this document? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ mt: 3, mb: 3, mr: 3 }}>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() => setOpenConfirm(false)}
          >
            Cancel
          </CommonButton>
          <CommonButton
            variant="contained"
            customColor="#007BFF"
            onClick={() => {
              deletefn(selectedDoc);
              setOpenConfirm(false);
            }}
          >
            Delete
          </CommonButton>
        </DialogActions>
      </Dialog>

      <TablePagination
        component="div"
        count={docTable.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif",
          },
        }}
      />
    </>
  );
}

export default DocumentSubmission;
